import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import _ from "lodash";
import type { ModeBarButtonAny } from "plotly.js";
import type { AucTableData } from "../../../../apis/types/analysisTypes";
import "./style.css";
import { PlotlyIcons } from "./plotlyIcons";
import { useTrialAnalysis } from "../../Trials/TrialAnalysis/TrialAnalysisContext";
import BoxWhiskerChart, { PLOT_HOVER_LINES } from "./BoxWhiskerChart";
import type { BoxWhiskerCategory, BoxWhiskerSeries } from "./BoxWhiskerChart";

type Props = {
	aucData?: AucTableData[];
	analysisName?: string;
	treatmentNames?: Record<number, string>;
	aucMeanComparisons?: {
		cv: string;
		mean: string;
		pValue: string;
		letteringPairs: { treatmentId: number; letter: string }[];
	};
};

/**
 * The trial timecourse AUC box and whisker chart. Reads the box statistics the analysis already
 * computed and hands them to {@link BoxWhiskerChart}.
 */
const TimecourseBoxWhiskerChart = ({ aucData, analysisName = "", treatmentNames, aucMeanComparisons }: Props) => {
	const entityNames = useSelector((state) => state.entityNames ?? null);
	const {
		pending: { selectedTreatmentMatrix },
		display,
		isAucOutdated,
		isLettersOutdated,
	} = useTrialAnalysis();

	const [disableColors, setDisableColors] = useState(false);
	const [showTreatmentNames, setShowTreatmentNames] = useState(false);
	const [groupPlots, setGroupPlots] = useState(display.isRCBD);

	const { categories, series } = useMemo(() => {
		if (!aucData?.length || !selectedTreatmentMatrix?.length) {
			return { categories: [] as BoxWhiskerCategory[], series: [] as BoxWhiskerSeries[] };
		}

		const sorted = _.sortBy(
			_.filter(aucData, (auc) => selectedTreatmentMatrix[auc.trialTreatmentId]),
			"trialTreatmentId",
		);

		// The letters are computed on the grouped plots, so they only apply to that view.
		const showLetters = groupPlots && !isAucOutdated && !isLettersOutdated;

		const categories: BoxWhiskerCategory[] = sorted.map((trt) => ({
			id: String(trt.trialTreatmentId),
			stats: groupPlots ? trt.groupedBoxStats : trt.ungroupedBoxStats,
			letter: showLetters
				? (aucMeanComparisons?.letteringPairs.find((lp) => lp.treatmentId === trt.trialTreatmentId)?.letter ?? "")
				: undefined,
			label: treatmentNames?.[trt.trialTreatmentId],
		}));

		const pointsByReplicate: Record<number, BoxWhiskerSeries["points"]> = {};
		for (const trt of sorted) {
			// use plot groups if plots are grouped otherwise spread each plot
			const units = groupPlots
				? trt.plotGroupAucs
				: _.flatMap(trt.plotGroupAucs, (pga) => {
						const { plotAucs, ...rest } = pga;
						return _.map(plotAucs, (pa) => ({ ...rest, meanAuc: pa.auc }));
					});

			for (const unit of units) {
				if (!pointsByReplicate[unit.plotReplicate]) pointsByReplicate[unit.plotReplicate] = [];
				pointsByReplicate[unit.plotReplicate].push({
					categoryId: String(trt.trialTreatmentId),
					y: unit.meanAuc,
					details: [unit.plotGroup ?? "", trt.trialTreatmentId, unit.plotReplicate],
				});
			}
		}

		const series: BoxWhiskerSeries[] = Object.entries(pointsByReplicate).map(([rep, points]) => ({
			name: rep,
			colorIndex: Number(rep) - 1,
			points,
			hoverLines: PLOT_HOVER_LINES,
		}));

		return { categories, series };
	}, [
		aucData,
		selectedTreatmentMatrix,
		groupPlots,
		isAucOutdated,
		isLettersOutdated,
		aucMeanComparisons,
		treatmentNames,
	]);

	const modeBarButtons = useMemo(() => {
		const buttons: ModeBarButtonAny[] = [];
		if (treatmentNames) {
			buttons.push({
				name: "toggle treatment names",
				title: "Toggle Treatment Names",
				icon: PlotlyIcons.title,
				attr: showTreatmentNames ? "names-active" : "names-inactive",
				click: () => {
					setShowTreatmentNames(!showTreatmentNames);
				},
			});
		}

		if (entityNames?.fieldScaleTrial) {
			buttons.push({
				name: "toggle treatment blocks",
				title: "Toggle Treatment Blocks",
				icon: PlotlyIcons.treatmentBlocks,
				attr: groupPlots ? "blocks-active" : "blocks-inactive",
				click: () => {
					setGroupPlots(!groupPlots);
				},
			});
		}

		buttons.push({
			name: "toggle chart colors",
			title: "Toggle Chart Colors",
			icon: PlotlyIcons.pallette,
			attr: disableColors ? "colors-inactive" : "colors-active",
			click: () => {
				setDisableColors(!disableColors);
			},
		});
		return buttons;
	}, [disableColors, entityNames?.fieldScaleTrial, groupPlots, showTreatmentNames, treatmentNames]);

	return (
		<BoxWhiskerChart
			categories={categories}
			series={series}
			title="Area Under the Curve (AUC)"
			yAxisTitle={"AUC " + analysisName}
			legendTitle="Replicates"
			loading={!aucData?.length}
			emptyMessage="There is no data selected for the box and whisker chart."
			disableColors={disableColors}
			showCategoryLabels={showTreatmentNames}
			// Grouped plots all sit at their treatment's center; ungrouped plots spread out.
			jitter={groupPlots ? 0 : 0.5}
			markerSize={groupPlots ? 8 : 4}
			flush
			modeBarButtons={modeBarButtons}
			snapshotFileName={`${entityNames?.trialName}_${/\(([^)]+)\)/.exec(analysisName)?.[1] ?? analysisName}_BoxWhisker`}
		/>
	);
};

export default TimecourseBoxWhiskerChart;
